import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'motion/react';
import {
  Rocket, Users, Search, ChevronRight, ChevronLeft, CheckCircle2,
  Trophy, Home, ShieldCheck, Target, Briefcase,
  Zap, Sparkles, ArrowRight, X, Mail
} from 'lucide-react';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { useCreateNode, useCheckNodeName, checkNodeNameAvailable } from '../../hooks/useNode';
import { useCurrentUser } from '../../contexts/AuthContext';
import { useTenant } from '../../contexts/TenantContext';
import { DEFAULT_ENABLED_MODULES } from '../../constants';
import { toast } from 'react-hot-toast';

const TEMPLATES = [
  { id: 'sports', name: 'Youth Sports / Little League', icon: Trophy, color: 'text-blue-500' },
  { id: 'hoa', name: 'HOA / Community Board', icon: Home, color: 'text-emerald-500' },
  { id: 'union', name: 'Labor Union / Local', icon: ShieldCheck, color: 'text-red-500' },
  { id: 'guardian', name: 'Guardian-Led (School/Pet)', icon: Users, color: 'text-amber-500' },
  { id: 'nonprofit', name: 'Non-Profit / Association', icon: Briefcase, color: 'text-purple-500' },
  { id: 'generic', name: 'Generic / Custom', icon: Target, color: 'text-gray-500' },
];

interface OnboardingWizardProps {
  onComplete: () => void;
}

export const OnboardingWizard: React.FC<OnboardingWizardProps> = ({ onComplete }) => {
  const navigate = useNavigate();
  const createNode = useCreateNode();
  const { isBackendSynced, isSyncing, retrySync } = useCurrentUser();
  const { switchOrg } = useTenant();
  const [step, setStep] = useState(1);
  const [intent, setIntent] = useState<'create' | 'join' | null>(null);
  const [orgName, setOrgName] = useState('');
  const { data: isNameAvailable, isLoading: isCheckingName } = useCheckNodeName(orgName);
  const [selectedTemplate, setSelectedTemplate] = useState('generic');
  const [operations, setOperations] = useState<'finance' | 'comms'>('comms');

  const totalSteps = intent === 'create' ? 5 : 4;

  const handleCreate = useCallback(async () => {
    if (!orgName.trim()) return;
    if (!isBackendSynced) {
      toast.error('Your account is still synchronizing with the server. Please wait or retry sync before creating an organization.', { id: 'sync-required' });
      return;
    }
    const available = await checkNodeNameAvailable(orgName);
    if (!available) {
      toast.error(`Organization name "${orgName}" is already taken. Please choose another name.`);
      setStep(3);
      return;
    }
    const slug = orgName.toLowerCase().replace(/[^a-z0-9]/g, '-').replace(/-+/g, '-');
    const modules = [...DEFAULT_ENABLED_MODULES];

    try {
      const created: any = await createNode.mutateAsync({
        name: orgName,
        slug,
        template: selectedTemplate.toUpperCase(),
        visibility: 'PUBLIC',
        tier: 'LOCAL',
        modules,
      });
      // Prefer the server's slug: the backend generates it from the name and ignores the
      // client-supplied value, so the two can disagree on punctuation and spacing.
      const newSlug = created?.slug || slug;
      localStorage.setItem('onboarding_completed', 'true');
      toast.success('Organization deployed successfully!');
      onComplete();
      switchOrg(newSlug, '/dashboard');
    } catch (err: any) {
      console.error(err);
      if (err?.response?.status === 404) {
        toast.error(
          'Your account is still finalizing setup with the server. Please wait a moment and try again.',
          { id: 'sync-required', duration: 5000 }
        );
      } else {
        toast.error(err?.response?.data?.message || 'Failed to create organization. Please try again.');
      }
    }
  }, [orgName, selectedTemplate, operations, createNode, switchOrg, navigate, onComplete]);

  const handleSkip = useCallback(() => {
    localStorage.setItem('onboarding_completed', 'true');
    onComplete();
  }, [onComplete]);

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.92, y: 30 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
        className="w-full max-w-3xl bg-white border-8 border-black shadow-[16px_16px_0px_#0044FF] dark:bg-black dark:border-white dark:shadow-[16px_16px_0px_#FFD600] overflow-hidden"
      >
        {/* Progress bar */}
        <div className="h-2 bg-gray-100 dark:bg-slate-800">
          <motion.div
            className="h-full bg-[#0044FF]"
            initial={{ width: 0 }}
            animate={{ width: `${(step / totalSteps) * 100}%` }}
            transition={{ duration: 0.3 }}
          />
        </div>

        {/* Header */}
        <div className="flex items-center justify-between px-8 py-5 border-b-4 border-black dark:border-white">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-[#FFD600] border-4 border-black flex items-center justify-center">
              <Rocket size={24} className="text-black" />
            </div>
            <div>
              <h2 className="text-xl font-black font-display uppercase tracking-tight text-black dark:text-white">
                Welcome to OrgSets
              </h2>
              <p className="text-[10px] font-mono uppercase tracking-widest text-gray-500">
                Step {step} of {totalSteps} • Setup Wizard
              </p>
            </div>
          </div>
          <button
            onClick={handleSkip}
            className="p-2 text-gray-400 hover:text-black dark:hover:text-white transition-colors"
            title="Skip onboarding"
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="p-8 md:p-12 min-h-[400px] flex flex-col">
          <AnimatePresence mode="wait">
            {/* Step 1: Welcome */}
            {step === 1 && (
              <motion.div
                key="welcome"
                initial={{ opacity: 0, x: 30 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -30 }}
                className="flex-1 flex flex-col items-center justify-center text-center space-y-8"
              >
                <div className="w-24 h-24 bg-[#0044FF] border-4 border-black flex items-center justify-center">
                  <Sparkles size={40} className="text-white" />
                </div>
                <div>
                  <h3 className="text-3xl md:text-4xl font-black font-display uppercase tracking-tighter text-black dark:text-white mb-4">
                    Let's Set Up Your Organization
                  </h3>
                  <p className="text-gray-500 font-mono text-sm uppercase tracking-widest max-w-md mx-auto leading-relaxed">
                    OrgSets gives your organization a professional command center with calendars, chat, finances, and more — all in one place.
                  </p>
                </div>
                <Button
                  onClick={() => setStep(2)}
                  className="bg-black text-white dark:bg-white dark:text-black font-black uppercase tracking-widest py-5 px-12 rounded-none border-4 border-black dark:border-white shadow-[6px_6px_0px_#0044FF] hover:shadow-[8px_8px_0px_#FFD600] transition-all text-sm"
                >
                  Let's Go <ArrowRight size={18} className="ml-2" />
                </Button>
              </motion.div>
            )}

            {/* Step 2: Intent */}
            {step === 2 && (
              <motion.div
                key="intent"
                initial={{ opacity: 0, x: 30 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -30 }}
                className="flex-1 space-y-8"
              >
                <div>
                  <h3 className="text-2xl font-black font-display uppercase tracking-tight text-black dark:text-white mb-2">
                    What Brings You Here?
                  </h3>
                  <p className="text-sm font-mono uppercase tracking-widest text-gray-500">
                    Choose your path
                  </p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <button
                    onClick={() => { setIntent('create'); setStep(3); }}
                    className="group p-8 border-4 border-black dark:border-white bg-white dark:bg-black text-left hover:bg-[#0044FF] hover:text-white transition-all shadow-[6px_6px_0px_#000000] dark:shadow-[6px_6px_0px_#FFFFFF] hover:shadow-[8px_8px_0px_#FFD600]"
                  >
                    <div className="w-14 h-14 bg-[#0044FF] group-hover:bg-white border-4 border-black flex items-center justify-center mb-6">
                      <Rocket size={28} className="text-white group-hover:text-[#0044FF]" />
                    </div>
                    <h4 className="text-xl font-black uppercase tracking-tight mb-2 text-black dark:text-white group-hover:text-white">
                      Create an Organization
                    </h4>
                    <p className="text-xs font-mono uppercase tracking-widest text-gray-500 group-hover:text-white/70 leading-relaxed">
                      I'm starting a new organization for my club, league, association, or community.
                    </p>
                    <ChevronRight size={24} className="mt-6 text-gray-300 group-hover:text-white" />
                  </button>

                  <button
                    onClick={() => { setIntent('join'); setStep(3); }}
                    className="group p-8 border-4 border-black dark:border-white bg-white dark:bg-black text-left hover:bg-[#FFD600] hover:text-black transition-all shadow-[6px_6px_0px_#000000] dark:shadow-[6px_6px_0px_#FFFFFF] hover:shadow-[8px_8px_0px_#0044FF]"
                  >
                    <div className="w-14 h-14 bg-[#FFD600] border-4 border-black flex items-center justify-center mb-6">
                      <Search size={28} className="text-black" />
                    </div>
                    <h4 className="text-xl font-black uppercase tracking-tight mb-2 text-black dark:text-white group-hover:text-black">
                      Join an Existing Organization
                    </h4>
                    <p className="text-xs font-mono uppercase tracking-widest text-gray-500 group-hover:text-black/70 leading-relaxed">
                      I was invited to join, or I'm looking for an organization to connect with.
                    </p>
                    <ChevronRight size={24} className="mt-6 text-gray-300 group-hover:text-black" />
                  </button>
                </div>
              </motion.div>
            )}

            {/* Step 3 (Create): Name + Template */}
            {step === 3 && intent === 'create' && (
              <motion.div
                key="create-name"
                initial={{ opacity: 0, x: 30 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -30 }}
                className="flex-1 space-y-8"
              >
                <div>
                  <h3 className="text-2xl font-black font-display uppercase tracking-tight text-black dark:text-white mb-2">
                    Name Your Organization
                  </h3>
                  <p className="text-sm font-mono uppercase tracking-widest text-gray-500">
                    This will be visible to your members
                  </p>
                </div>
                <div className="space-y-2">
                  <Input
                    placeholder="e.g. Springfield Little League"
                    value={orgName}
                    onChange={(e) => setOrgName(e.target.value)}
                    autoFocus
                  />
                  {orgName.trim().length >= 2 && (
                    <div className="flex items-center gap-2 text-xs font-mono pt-1">
                      {isCheckingName ? (
                        <span className="text-gray-500">Checking availability...</span>
                      ) : isNameAvailable === false ? (
                        <span className="text-red-600 font-bold flex items-center gap-1">
                          <X size={14} /> Name is already taken. Please choose another.
                        </span>
                      ) : isNameAvailable === true ? (
                        <span className="text-emerald-600 font-bold flex items-center gap-1">
                          <CheckCircle2 size={14} /> Name is available!
                        </span>
                      ) : null}
                    </div>
                  )}
                </div>
                <div>
                  <p className="text-[10px] font-black uppercase tracking-[0.2em] text-gray-400 font-mono mb-4">
                    Select Template
                  </p>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {TEMPLATES.map((t) => {
                      const IconComp = t.icon;
                      return (
                        <button
                          key={t.id}
                          onClick={() => setSelectedTemplate(t.id)}
                          className={`p-4 border-2 text-left transition-all ${
                            selectedTemplate === t.id
                              ? 'border-[#0044FF] bg-[#0044FF]/5 shadow-[4px_4px_0px_#0044FF]'
                              : 'border-black dark:border-white hover:border-[#0044FF]'
                          }`}
                        >
                          <IconComp size={18} className={`${t.color} mb-2`} />
                          <span className="text-xs font-bold uppercase tracking-tight text-black dark:text-white block">
                            {t.name}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              </motion.div>
            )}

            {/* Step 3 (Join): Browse / Search */}
            {step === 3 && intent === 'join' && (
              <motion.div
                key="join"
                initial={{ opacity: 0, x: 30 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -30 }}
                className="flex-1 space-y-8"
              >
                <div>
                  <h3 className="text-2xl font-black font-display uppercase tracking-tight text-black dark:text-white mb-2">
                    Join Your Organization
                  </h3>
                  <p className="text-sm font-mono uppercase tracking-widest text-gray-500">
                    Hubs are private and invite-only
                  </p>
                </div>
                <div className="space-y-6">
                  <button
                    onClick={() => {
                      localStorage.setItem('onboarding_completed', 'true');
                      onComplete();
                      navigate('/invitations');
                    }}
                    className="w-full p-6 border-4 border-black dark:border-white bg-white dark:bg-black text-left hover:bg-[#FFD600] hover:text-black transition-all shadow-[4px_4px_0px_#000000] dark:shadow-[4px_4px_0px_#FFFFFF] group"
                  >
                    <div className="flex items-center gap-4">
                      <Mail size={24} />
                      <div>
                        <h4 className="font-black uppercase tracking-tight text-lg">Check Pending Invitations</h4>
                        <p className="text-xs font-mono uppercase tracking-widest text-gray-500 group-hover:text-black/70 mt-1">
                          View and accept invites sent to your email
                        </p>
                      </div>
                    </div>
                  </button>
                </div>
              </motion.div>
            )}

            {/* Step 4 (Create): Operations Mode */}
            {step === 4 && intent === 'create' && (
              <motion.div
                key="create-ops"
                initial={{ opacity: 0, x: 30 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -30 }}
                className="flex-1 space-y-8"
              >
                <div>
                  <h3 className="text-2xl font-black font-display uppercase tracking-tight text-black dark:text-white mb-2">
                    Operations Mode
                  </h3>
                  <p className="text-sm font-mono uppercase tracking-widest text-gray-500">
                    Choose what modules to start with
                  </p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <button
                    onClick={() => setOperations('comms')}
                    className={`p-6 border-4 text-left transition-all ${
                      operations === 'comms'
                        ? 'border-[#0044FF] bg-[#0044FF]/5 shadow-[6px_6px_0px_#0044FF]'
                        : 'border-black dark:border-white hover:border-[#0044FF]'
                    }`}
                  >
                    <Zap size={28} className={operations === 'comms' ? 'text-[#0044FF]' : 'text-gray-400'} />
                    <h4 className="font-black uppercase tracking-tight mt-4 mb-2 text-black dark:text-white">Community & Events</h4>
                    <p className="text-xs font-mono uppercase tracking-widest text-gray-500 leading-relaxed">
                      Discussions, member directory, calendar, and pulse broadcast alerts.
                    </p>
                  </button>
                  <button
                    onClick={() => setOperations('finance')}
                    className={`p-6 border-4 text-left transition-all ${
                      operations === 'finance'
                        ? 'border-[#0044FF] bg-[#0044FF]/5 shadow-[6px_6px_0px_#0044FF]'
                        : 'border-black dark:border-white hover:border-[#0044FF]'
                    }`}
                  >
                    <ShieldCheck size={28} className={operations === 'finance' ? 'text-[#0044FF]' : 'text-gray-400'} />
                    <h4 className="font-black uppercase tracking-tight mt-4 mb-2 text-black dark:text-white">Service & Operations</h4>
                    <p className="text-xs font-mono uppercase tracking-widest text-gray-500 leading-relaxed">
                      Help desk support, member coordination, and structured organizational governance.
                    </p>
                  </button>
                </div>
              </motion.div>
            )}

            {/* Step 4 (Join): Quick Tour */}
            {step === 4 && intent === 'join' && (
              <motion.div
                key="join-tour"
                initial={{ opacity: 0, x: 30 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -30 }}
                className="flex-1 flex flex-col items-center justify-center text-center space-y-8"
              >
                <div className="w-20 h-20 bg-green-500 border-4 border-black flex items-center justify-center">
                  <CheckCircle2 size={40} className="text-white" />
                </div>
                <div>
                  <h3 className="text-3xl font-black font-display uppercase tracking-tighter text-black dark:text-white mb-3">
                    You're All Set!
                  </h3>
                  <p className="text-sm font-mono uppercase tracking-widest text-gray-500 max-w-md mx-auto leading-relaxed">
                    Once you join an organization, your dashboard will populate with announcements, events, and everything your group shares.
                  </p>
                </div>
              </motion.div>
            )}

            {/* Step 5 (Create): Review & Deploy */}
            {step === 5 && intent === 'create' && (
              <motion.div
                key="create-review"
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="flex-1 flex flex-col items-center justify-center text-center space-y-8"
              >
                <div className="w-20 h-20 bg-[#FFD600] border-4 border-black flex items-center justify-center">
                  <Rocket size={40} className="text-black" />
                </div>
                <div>
                  <h3 className="text-3xl font-black font-display uppercase tracking-tighter text-black dark:text-white mb-3">
                    Ready to Launch
                  </h3>
                  <p className="text-sm font-mono uppercase tracking-widest text-gray-500 mb-6">
                    Deploying "{orgName}"
                  </p>
                </div>
                <div className="w-full max-w-sm bg-[#F5F5F0] dark:bg-slate-900 border-4 border-black dark:border-white p-6 space-y-4 text-left">
                  <div className="flex justify-between text-xs">
                    <span className="font-mono uppercase tracking-widest text-gray-400">Name</span>
                    <span className="font-bold text-black dark:text-white">{orgName}</span>
                  </div>
                  <div className="flex justify-between text-xs">
                    <span className="font-mono uppercase tracking-widest text-gray-400">Template</span>
                    <span className="font-bold text-black dark:text-white">{TEMPLATES.find(t => t.id === selectedTemplate)?.name}</span>
                  </div>
                  <div className="flex justify-between text-xs">
                    <span className="font-mono uppercase tracking-widest text-gray-400">Modules</span>
                    <span className="font-bold text-[#0044FF]">{DEFAULT_ENABLED_MODULES.length} Core Modules (Active)</span>
                  </div>
                  <div className="flex justify-between text-xs">
                    <span className="font-mono uppercase tracking-widest text-gray-400">Tier</span>
                    <span className="font-bold text-black dark:text-white">Free</span>
                  </div>
                  <div className="border-t-2 border-dashed border-gray-300 dark:border-gray-700 pt-3">
                    <p className="text-[11px] font-mono text-gray-600 dark:text-gray-300 flex items-center gap-1.5">
                      <Sparkles size={14} className="text-[#0044FF] shrink-0" />
                      Next step: Invite founding members to your new organization right from your dashboard!
                    </p>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Footer */}
        <div className="px-8 py-5 border-t-4 border-black dark:border-white bg-[#F5F5F0] dark:bg-slate-900 flex items-center justify-between">
          <button
            onClick={() => {
              if (step === 1) { handleSkip(); return; }
              if (step === 3 && intent) { setIntent(null); setStep(2); return; }
              setStep(s => s - 1);
            }}
            className="text-xs font-bold uppercase tracking-widest text-gray-500 hover:text-black dark:hover:text-white transition-colors flex items-center gap-2"
          >
            <ChevronLeft size={16} />
            {step === 1 ? 'Skip' : 'Back'}
          </button>

          {/* Next / Deploy button */}
          {step === 5 && intent === 'create' ? (
            <div className="flex items-center gap-2">
              {!isBackendSynced && (
                <button
                  type="button"
                  onClick={() => retrySync(false)}
                  className="bg-[#FFD600] text-black font-black uppercase tracking-wider py-4 px-4 rounded-none border-4 border-black text-[10px] hover:bg-black hover:text-white transition-colors"
                >
                  Retry Sync
                </button>
              )}
              <Button
                onClick={handleCreate}
                isLoading={createNode.isPending || isSyncing}
                disabled={!isBackendSynced}
                className="bg-[#0044FF] text-white font-black uppercase tracking-widest py-4 px-10 rounded-none border-4 border-black shadow-[4px_4px_0px_#000000] text-xs hover:bg-black transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {!isBackendSynced ? 'Sync Required' : 'Deploy Organization'} <ArrowRight size={16} className="ml-2" />
              </Button>
            </div>
          ) : step === 4 && intent === 'join' ? (
            <Button
              onClick={() => { localStorage.setItem('onboarding_completed', 'true'); onComplete(); }}
              className="bg-black text-white dark:bg-white dark:text-black font-black uppercase tracking-widest py-4 px-10 rounded-none border-4 border-black dark:border-white shadow-[4px_4px_0px_#0044FF] text-xs"
            >
              Go to Dashboard <ArrowRight size={16} className="ml-2" />
            </Button>
          ) : (
            <Button
              onClick={() => setStep(s => s + 1)}
              disabled={step === 3 && intent === 'create' && (!orgName.trim() || isCheckingName || isNameAvailable === false)}
              className="bg-black text-white dark:bg-white dark:text-black font-black uppercase tracking-widest py-4 px-10 rounded-none border-4 border-black dark:border-white shadow-[4px_4px_0px_#0044FF] text-xs disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Next <ChevronRight size={16} className="ml-2" />
            </Button>
          )}
        </div>
      </motion.div>
    </div>
  );
};
